import { Type } from "@sinclair/typebox";
import { quoteWorkingPortionSnapshotSchema } from "./quote-working-portion.js";
export const mediaUuidSchema = Type.String({ format: "uuid" });
export const mediaSha256Schema = Type.String({ pattern: "^[0-9a-f]{64}$" });
export const mediaDateSchema = Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" });
export const mediaTimestampSchema = Type.String({ format: "date-time" });
export const roofContextVisualSelectionSchema = Type.Object({
    role: Type.Literal("roof_context"),
    presentationSpecId: mediaUuidSchema,
    presentationSpecSha256: mediaSha256Schema
}, { additionalProperties: false });
export const uploadVisualAssetMetadataSchema = Type.Object({
    sourceKind: Type.Literal("user_upload"),
    sourceCapturedAt: Type.Optional(mediaDateSchema),
    rightsDeclaration: Type.Literal("authorized_by_uploader"),
    attribution: Type.Optional(Type.String({ minLength: 1, maxLength: 500 })),
    intendedUse: Type.Literal("quote_pdf")
}, { additionalProperties: false });
export const createMapSnapshotSchema = Type.Object({
    sourcePolicyVersion: Type.Literal("map-visuals-2026-09"),
    framing: Type.Literal("roof_context"),
    interventionGeometry: Type.Optional(quoteWorkingPortionSnapshotSchema.properties.geometry),
    context: Type.Literal("standard"),
    boundingBox: Type.Optional(Type.Tuple([
        Type.Number({ minimum: -180, maximum: 180 }),
        Type.Number({ minimum: -85.0511, maximum: 85.0511 }),
        Type.Number({ minimum: -180, maximum: 180 }),
        Type.Number({ minimum: -85.0511, maximum: 85.0511 })
    ]))
}, { additionalProperties: false });
export const visualAssetStatusSchema = Type.Union([
    Type.Literal("validating"),
    Type.Literal("ready"),
    Type.Literal("rejected"),
    Type.Literal("deleted")
]);
export const visualAssetAcceptedSchema = Type.Object({
    assetId: mediaUuidSchema,
    status: Type.Literal("validating")
}, { additionalProperties: false });
export const visualAssetSchema = Type.Object({
    id: mediaUuidSchema,
    kind: Type.Union([Type.Literal("photo_original"), Type.Literal("map_snapshot")]),
    status: visualAssetStatusSchema,
    revision: Type.Integer({ minimum: 1 }),
    reviewState: Type.Union([
        Type.Literal("unreviewed"),
        Type.Literal("approved"),
        Type.Literal("rejected"),
        Type.Literal("stale")
    ]),
    rightsState: Type.Union([
        Type.Literal("pending"),
        Type.Literal("approved_pdf"),
        Type.Literal("rejected")
    ]),
    rightsEvidenceId: Type.Union([mediaUuidSchema, Type.Null()]),
    width: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    height: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    source: Type.Object({
        kind: Type.Union([Type.Literal("user_upload"), Type.Literal("mapbox_static")]),
        capturedAt: Type.Union([mediaDateSchema, Type.Null()]),
        license: Type.Union([Type.String(), Type.Null()]),
        attribution: Type.Union([Type.String(), Type.Null()])
    }, { additionalProperties: false }),
    createdAt: mediaTimestampSchema
}, { additionalProperties: false });
export const visualAssetListSchema = Type.Object({ items: Type.Array(visualAssetSchema) }, { additionalProperties: false });
export const updateVisualAssetMetadataSchema = Type.Object({
    expectedRevision: Type.Integer({ minimum: 1 }),
    sourceCapturedAt: Type.Optional(Type.Union([mediaDateSchema, Type.Null()])),
    attribution: Type.Optional(Type.Union([Type.String({ minLength: 1, maxLength: 500 }), Type.Null()]))
}, { additionalProperties: false });
export const rightsUseScopeSchema = Type.Literal("quote_pdf");
export const reviewVisualRightsSchema = Type.Object({
    expectedRevision: Type.Integer({ minimum: 1 }),
    decision: Type.Union([Type.Literal("approve"), Type.Literal("reject")]),
    useScopes: Type.Array(rightsUseScopeSchema, { uniqueItems: true, maxItems: 1 }),
    rightsEvidenceId: mediaUuidSchema,
    rightsPolicyVersion: Type.Literal("visual-sources-2026-09"),
    reason: Type.String({ minLength: 2, maxLength: 1000 })
}, { additionalProperties: false });
export const focalPointSchema = Type.Object({
    x: Type.Number({ minimum: 0, maximum: 1 }),
    y: Type.Number({ minimum: 0, maximum: 1 })
}, { additionalProperties: false });
export const createPresentationSpecSchema = Type.Object({
    sourceAssetId: mediaUuidSchema,
    aspectRatio: Type.Literal("16:9"),
    focalPoint: focalPointSchema,
    zoom: Type.Number({ minimum: 1, maximum: 4 }),
    acceptQualityWarning: Type.Boolean()
}, { additionalProperties: false });
export const derivedCropSchema = Type.Object({
    x: Type.Number({ minimum: 0, maximum: 1 }),
    y: Type.Number({ minimum: 0, maximum: 1 }),
    width: Type.Number({ exclusiveMinimum: 0, maximum: 1 }),
    height: Type.Number({ exclusiveMinimum: 0, maximum: 1 })
}, { additionalProperties: false });
export const presentationQualitySchema = Type.Union([
    Type.Literal("recommended"),
    Type.Literal("good"),
    Type.Literal("warning_accepted")
]);
export const presentationSpecSchema = Type.Object({
    presentationSpecId: mediaUuidSchema,
    presentationSpecSha256: mediaSha256Schema,
    pdfRenditionAssetId: mediaUuidSchema,
    derivedCrop: derivedCropSchema,
    effectiveWidth: Type.Integer({ minimum: 1280 }),
    effectiveHeight: Type.Integer({ minimum: 720 }),
    qualityState: presentationQualitySchema
}, { additionalProperties: false });
export const mediaProblemSchema = Type.Object({
    type: Type.String(),
    title: Type.String(),
    status: Type.Integer({ minimum: 400, maximum: 599 }),
    detail: Type.String(),
    instance: Type.Optional(Type.String()),
    errorCode: Type.String(),
    effectiveWidth: Type.Optional(Type.Integer({ minimum: 1 })),
    effectiveHeight: Type.Optional(Type.Integer({ minimum: 1 })),
    qualityState: Type.Optional(Type.Union([Type.Literal("warning"), Type.Literal("insufficient")]))
}, { additionalProperties: false });
//# sourceMappingURL=media.js.map